import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import arrow_right_icon from '../../../assets/arrow_right_icon.svg';
import delete_icon from '../../../assets/delete_icon.svg';
import { Carousel } from '../../../component/carousel';
import { useEditPromotionsMutation } from '../../../store/promotion/api';
import { IPromotion } from '../../../store/promotion/interface';
import { getDateString } from '../../../utils/date';
import { pxToRem } from '../../../utils/pxToRem';
import { showErrorToast, showSuccessToast } from '../../../utils/toast';

type IPromotionListCard = {
  handleDelete: () => void;
  promotionItem: IPromotion;
};
export const PromotionListCard: FC<IPromotionListCard> = ({
  handleDelete,
  promotionItem
}) => {
  const navigate = useNavigate();
  const handleViewPromotion = () => {
    navigate(`/promotions/view/${promotionItem._id}`, {
      state: { promotionId: promotionItem._id }
    });
  };
  const [changeStatus, { isLoading: isChangeStatusLoading }] =
    useEditPromotionsMutation();
  const [activeState, setActiveState] = useState(promotionItem.status);
  const handleActivityStatus = (promotionId: any) => {
    const newStatus = promotionItem.status === 'active' ? 'inactive' : 'active';
    const formData = new FormData();
    formData.append('status', newStatus);
    setActiveState(newStatus);
    changeStatus({ id: promotionId, body: formData })
      .unwrap()
      .then((result) => {
        showSuccessToast(result.message);
        setActiveState(promotionItem.status);
      })
      .catch((error) => {
        showErrorToast(error.message);
        setActiveState(promotionItem.status);
      });
  };
  return (
    <Card>
      <PromotionImage images={promotionItem.files} />
      <DeleteBtn onClick={handleDelete}>
        <Icon src={delete_icon} />
      </DeleteBtn>
      <PromotionDetails>
        <PromotionHeader>
          <PromotionName>{promotionItem.vendorName}</PromotionName>
          <ActivationContainer>
            <Switch>
              <Input
                type="checkbox"
                onClick={() => handleActivityStatus(promotionItem._id)}
                checked={activeState === 'active'}
                readOnly
              />
              <SliderRound id={'slider'} />
            </Switch>
            {isChangeStatusLoading
              ? 'loading ...'
              : activeState === 'active'
              ? 'Active'
              : 'Inactive'}
          </ActivationContainer>
        </PromotionHeader>
        <DurationWrap>
          <DurationText>Duration: </DurationText>
          <Date>{getDateString(promotionItem.duration.from)}</Date>{' '}
          <ToText>to</ToText>{' '}
          <Date>{getDateString(promotionItem.duration.to)}</Date>
        </DurationWrap>

        <PromotionDescription>{promotionItem.description}</PromotionDescription>
        <ViewBtn onClick={handleViewPromotion}>
          <p>View</p>
          <Icon src={arrow_right_icon} />
        </ViewBtn>
      </PromotionDetails>
    </Card>
  );
};
const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  position: relative;
  padding: 8px 8px 35px 8px;
  width: calc(30.33% - 16px);
  max-height: 498px;
  margin: 0px 10px;
  margin-bottom: 48px;
  @media (max-width: 1000px) {
    width: calc(45% - 16px);
    margin: 0px 15px;
    margin-bottom: 48px;
  }
  @media (max-width: 730px) {
    width: 100%;
    margin: 0;
    margin-bottom: 48px;
  }
`;
const PromotionImage = styled(Carousel)`
  border-radius: 16px;
  width: 100%;
  height: 200px;
  max-width: 875px;
  object-fit: cover;
  background-color: #c4c4c4;
`;
const DeleteBtn = styled.button`
  position: absolute;
  right: 24px;
  top: 16px;
  background-color: transparent;
  outline: none;
  border: none;
  padding: 0;
  & :hover {
    opacity: 0.6;
  }
`;
const Icon = styled.img`
  max-height: 48px;
  max-width: 48px;
`;
const PromotionDetails = styled.div`
  padding: 23px 16px 0px 16px;
`;

const PromotionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
const DurationWrap = styled.div`
  display: flex;
  align-items: center;
`;
const DurationText = styled.p`
  font-weight: 500;
  font-size: ${pxToRem(12)};
  line-height: ${pxToRem(14)};
  letter-spacing: ${pxToRem(0.15)};
  color: #32324b;
`;
const ToText = styled(DurationText)``;
const Date = styled(DurationText)`
  color: #a1a1a1;
  margin: 0px 4px;
`;
const PromotionName = styled.p`
  font-size: ${pxToRem(14)};
  line-height: ${pxToRem(20)};
  font-style: normal;
  font-weight: 500;
  color: #32324b;
  text-align: left;
  text-transform: capitalize;
`;
const PromotionDescription = styled(PromotionName)`
  font-size: ${pxToRem(14)};
  line-height: ${pxToRem(23)};
  font-weight: 400;
  margin: 16px 0px;
  color: #425466;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;
const ViewBtn = styled.button`
  font-style: normal;
  font-weight: 500;
  font-size: ${pxToRem(12)};
  line-height: 150%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  padding: 7px 12px;
  outline: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.sojiOrange};
  & :hover {
    opacity: 0.6;
  }
`;
const SliderRound = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ec2b00;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 12px;
  ::before {
    border-radius: 50%;
    position: absolute;
    content: '';
    height: 12px;
    width: 12px;
    left: 4px;
    bottom: 2px;
    top: 2px;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.4s;
  }
`;
const Input = styled.input`
  :focus + ${SliderRound} {
    box-shadow: 0 0 1px #2f7ff6;
  }
  :checked + ${SliderRound} {
    background-color: #2f7ff6;
    ::before {
      -webkit-transform: translateX(12px);
      -ms-transform: translateX(12px);
      transform: translateX(12px);
    }
  }
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 16px;
  margin-right: 6px;
  ${Input} {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;
const ActivationContainer = styled.div`
  display: flex;
  align-self: flex-end;
  justify-content: flex-end;
  align-items: center;

  font-size: ${pxToRem(12)};
  font-style: normal;
  font-weight: 500;
  line-height: ${pxToRem(14)};
  letter-spacing: ${pxToRem(0.15)};
  color: #32324b;
  text-transform: capitalize;
  margin: 0px 6px;
`;
